import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getScreenViewHorizontal, getValueByHeaderType } from '../functions';
import useWidthGant from '../hooks/useWidthGant';
import { RootState } from '../store';
import { GroupListOfTimePeriodType, HeaderType, ModeTimeLineType, RowGantType } from '../types';
import GantGridCell from './gantGridCell';
import { useScrollContext } from './scrollContext';

interface IGantGridColumn {
    heightRow: number;
    dates: DateTime[];
    rowType: RowGantType | HeaderType;
    isShowText?: boolean;
    widthLeftHeader: number;
    height: number;
}

const GantGridColumn: React.FC<IGantGridColumn> = ({
    dates,
    rowType,
    isShowText,
    heightRow,
    height,
    widthLeftHeader,
}) => {
    const widthScreenForGant = useWidthGant();
    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);
    const modeTimeLine = useSelector<RootState, ModeTimeLineType>((state) => state.gant.modeTimeLine);
    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const availableWidth = useSelector<RootState, number>((state) => state.gant.availableWidth);

    const { scrollX: currentScrollX } = useScrollContext();
    const isShortFormLabel =
        rowType === 'month' && (headerType === 'year' || headerType === 'quarter') && modeTimeLine !== 'pagination';
    const isSmallSizeLabel = rowType === 'weekNum' && headerType === 'quarter' && modeTimeLine !== 'pagination';

    const renderColumnLine = useMemo(() => {
        const { start, end } = getScreenViewHorizontal(currentScrollX, widthColumn, availableWidth, widthLeftHeader);
        const dateListForShowing = dates.slice(start, end);

        const groupingData = (dateListForShowing: DateTime[], start: number) => {
            const groupListOfTimePeriod: GroupListOfTimePeriodType[] = [];

            let oldValue = '';
            let _oldValueDay = '';
            let oldValueTip = '';
            let oldIndex = 0;

            let oldLeftBorder = start * widthColumn;

            const leftBorderScreen = currentScrollX;
            const rightBorderScreen = currentScrollX + widthScreenForGant;

            const storeLabel: { [key: string]: string } = {};

            const getDataForTimePeriod = (date?: DateTime) => {
                const label = rowType === 'dayWeek' ? storeLabel[oldValue]?.charAt(0) : storeLabel[oldValue];
                const tip = oldValueTip;

                let isWeekend = false;

                if (['day', 'dayWeek'].includes(rowType) && date) {
                    isWeekend = [1, 7].includes(date.weekday);
                }

                return { label, isWeekend, tip };
            };

            dateListForShowing.forEach((date, index) => {
                const newValue = getValueByHeaderType(rowType, date, isShortFormLabel);

                if (!storeLabel[newValue.valueGroup]) {
                    storeLabel[newValue.valueGroup] = newValue.valueDisplay;
                }

                if (oldValue && oldValue !== newValue.valueGroup) {
                    const leftBorder = oldLeftBorder < leftBorderScreen ? leftBorderScreen : oldLeftBorder;
                    const rightBorder =
                        oldLeftBorder + (index - oldIndex) * widthColumn > rightBorderScreen
                            ? rightBorderScreen
                            : oldLeftBorder + (index - oldIndex) * widthColumn;
                    groupListOfTimePeriod.push({
                        key: `${rowType}${date.toSeconds()}`,
                        length: index - oldIndex,
                        ...getDataForTimePeriod(date),
                        leftBorder,
                        rightBorder,
                    });
                    oldLeftBorder = rightBorder;
                    oldIndex = index;
                }

                oldValue = newValue.valueGroup;
                const valueByHeaderType = getValueByHeaderType(rowType, date, isShortFormLabel);
                _oldValueDay = valueByHeaderType.valueDisplay;
                oldValueTip = valueByHeaderType.valueTip || '';
            });

            let newLength = dateListForShowing.length - oldIndex;

            if ((dateListForShowing.length - oldIndex) * widthColumn > widthScreenForGant) {
                newLength--;
            }

            const leftBorder = oldLeftBorder < leftBorderScreen ? leftBorderScreen : oldLeftBorder;
            const rightBorder =
                oldLeftBorder + newLength * widthColumn > rightBorderScreen
                    ? rightBorderScreen
                    : oldLeftBorder + newLength * widthColumn;

            groupListOfTimePeriod.push({
                key: `${rowType}${oldValue}`,
                length: newLength,
                ...getDataForTimePeriod(),
                leftBorder,
                rightBorder,
            });

            return groupListOfTimePeriod.filter((period) => period.length >= 0);
        };

        const groupListOfTimePeriod = groupingData(dateListForShowing, start);

        const cells: any[] = [];
        let currentStartIndex = start;

        groupListOfTimePeriod.forEach((groupTimePeriod) => {
            const lengthGroup = groupTimePeriod.rightBorder - groupTimePeriod.leftBorder;
            const currentX = groupTimePeriod.leftBorder + lengthGroup / 2;

            cells.push(
                <GantGridCell
                    key={groupTimePeriod.key}
                    groupTimePeriod={groupTimePeriod}
                    currentStartIndex={currentStartIndex}
                    heightRow={heightRow}
                    height={height}
                    isShowText={isShowText}
                    currentX={currentX}
                    lengthGroup={lengthGroup}
                    widthColumn={widthColumn}
                    isSmallSizeLabel={isSmallSizeLabel}
                />,
            );
            currentStartIndex += groupTimePeriod.length;
        });

        return <g>{cells}</g>;
    }, [dates, currentScrollX, widthColumn, availableWidth, rowType, heightRow, isShowText, widthLeftHeader]);

    return <>{renderColumnLine}</>;
};

export default React.memo(GantGridColumn);
